import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

@Injectable({
  providedIn: 'root',
})
export class DateFormatService {
  constructor() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
  }

  /**
   * Formatea una fecha relativa usando dayjs
   * Retorna: "Ahora", "Hoy HH:mm", "Ayer HH:mm", o "DD/MM/YY HH:mm"
   */
  formatRelativeDateUtc(date: any): string {
    // Sin fecha no hay nada que mostrar. El guard explícito es necesario porque
    // dayjs(undefined) devuelve "ahora", no una fecha inválida.
    if (date === null || date === undefined || date === '') {
      return '';
    }

    // Parseamos la fecha respetando su zona horaria original
    const dayjsDate = dayjs(date); // Sin .utc() para respetar la zona horaria original
    if (!dayjsDate.isValid()) {
      return '';
    }

    const now = dayjs();
    const diffMinutes = now.diff(dayjsDate, 'minute');

    // Usamos valor absoluto para que un desfase de reloj de pocos segundos hacia el
    // futuro siga mostrando "Ahora" en vez de caer en la rama de fecha completa.
    if (Math.abs(diffMinutes) < 2) {
      return 'Ahora';
    }

    // GLOB-1887: la comparación debe ser por día de calendario, no por delta de horas.
    // `now.diff(date, 'day')` trunca la diferencia absoluta, así que un mensaje de ayer
    // a las 23:00 visto hoy a las 08:00 daba 0 y se etiquetaba "Hoy". `isSame(..., 'day')`
    // es además inmune a los saltos de horario de verano (días de 23 y 25 horas).
    if (dayjsDate.isSame(now, 'day')) {
      return `Hoy ${dayjsDate.format('HH:mm')}`;
    }
    if (dayjsDate.isSame(now.subtract(1, 'day'), 'day')) {
      return `Ayer ${dayjsDate.format('HH:mm')}`;
    }
    return `${dayjsDate.format('DD/MM/YY')} ${dayjsDate.format('HH:mm')}`;
  }

  /**
   * Formatea una fecha para grupos de conversación usando Date nativo
   * Retorna: "Hoy", "Ayer", o fecha completa en español
   */
  formatGroupDate(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Hoy';
    if (date.toDateString() === yesterday.toDateString()) return 'Ayer';

    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}
